import UserAuthorization from './UserAuthorization';
import { BuildingDataResponse } from '@/ts/HococoInterfaces';

export default class BuildingData {
    public id: number = -1;
    public name: string = '';
    public numberOfProperties: number;
    public managers: number[] = [];
    public projectId: number;
    public staff: number[] = [];
    public selected: boolean;
    public authorizedUsers: { [index: number]: number[] } = {};
    public authorizationString: string = '';

    constructor(
        id: number,
        name: string = '',
        numberOfProperties: number = 0,
        projectId: number = null,
    ) {
        this.id = id;
        this.name = name;
        this.numberOfProperties = numberOfProperties;
        this.projectId = projectId;
        this.selected = false;
    }

    get persisted(): boolean {
        return (this.id !== -1);
    }

    public requireUpdate(buildingResponse: BuildingDataResponse): boolean {
        const propertiesCount = buildingResponse.numberOfProperties ? buildingResponse.numberOfProperties.split(',').length : 0;

        return (
            this.numberOfProperties !== propertiesCount
        );
    }

    public updateLocalBuildingData(buildingResponse: BuildingDataResponse): void {
        if (this.requireUpdate(buildingResponse)) {
            this.numberOfProperties = buildingResponse.numberOfProperties ? buildingResponse.numberOfProperties.split(',').length : 0;
        }
    }

    public populateManagerIds() {
        const managerRoleId = window.hoc.userRoleKeys['facility-manager'];
        const result = [];

        for (const userId in this.authorizedUsers) {
            if (this.authorizedUsers[userId].includes(managerRoleId)) {
                result.push(parseInt(userId, 10));
            }
        }

        this.managers = result;
    }

    public dataEquals(building: BuildingData): boolean {
        // Check if this object's values are equal to the given object's
        return (
            this.id === building.id &&
            this.name === building.name &&
            this.numberOfProperties === building.numberOfProperties &&
            this.projectId === building.projectId &&
            this.authEquals(building.authorizedUsers)
        );
    }

    public cloneData(building: BuildingData) {
        this.name = building.name;
        this.numberOfProperties = building.numberOfProperties;
        this.managers = building.managers;
        this.projectId = building.projectId;
        this.staff = building.staff;
        this.selected = building.selected;
        this.authorizedUsers = building.authorizedUsers;
    }

    public addAuthorization(userId: number, roleId: number) {
        if (typeof this.authorizedUsers[userId] === 'undefined') {
            this.authorizedUsers[userId] = [roleId];
        } else {
            if (this.authorizedUsers[userId].includes(roleId)) {
                return;
            }

            this.authorizedUsers[userId].push(roleId);
            this.authorizedUsers[userId].sort();
        }

        this.populateManagerIds();
    }

    public addManager(userId: number) {
        this.addAuthorization(userId, window.hoc.userRoleKeys['facility-manager']);
    }

    public removeManager(userId: number) {
        this.removeAuthorization(userId, window.hoc.userRoleKeys['facility-manager']);
    }

    public removeAuthorization(userId: number, roleId: number) {
        if (typeof this.authorizedUsers[userId] !== 'undefined' && this.authorizedUsers[userId].indexOf(roleId) !== -1) {
            this.authorizedUsers[userId] = this.authorizedUsers[userId]
                .filter((role) => roleId !== roleId);
            this.authorizedUsers[userId].sort();
            this.populateManagerIds();
        }
    }

    private authEquals(auth: any): boolean {
        if (Object.keys(this.authorizedUsers).length !== Object.keys(auth).length) {
            return false;
        }

        const ownKeys = Object.keys(this.authorizedUsers).slice(0).sort();
        const theirKeys = Object.keys(auth).slice(0).sort();

        if (JSON.stringify(ownKeys) !== JSON.stringify(theirKeys)) {
            return false;
        }

        for (const userId in this.authorizedUsers) {
            if (JSON.stringify(this.authorizedUsers[userId]) !== JSON.stringify(auth[userId])) {
                return false;
            }
        }

        return true;
    }
}

